import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import { Row, Col } from 'react-bootstrap';
import { mapPlaylistFromVideoSearch, LoadingSpinner, Dropdown, Button, Icon, internationalizeDate } from 'yankees-react-utils';
import { useOktaQueries, PlayerReport } from '../../../../data';
import { getRowData, getSplitRows } from './utils';
import ReportCard from '../../../../components/ReportCard';
import BlockingChart from '../../../../components/BlockingChart';
import VideoPlayer from '../../../../components/VideoPlayer';
import BlockingReportTable from './BlockingReportTable';
import { store } from '../../../../store';

const StyledRow = styled(Row)`
    margin: 10px;
`;
const StyledFiltersContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    > div {
        display: flex;
    }
    > div > * {
        margin: 5px;
    }
`;
const StyledFiltersLabel = styled.div`
    font-size: 12px;
    font-weight: bold;
    margin-top: auto;
    margin-bottom: auto;
    padding: 0 10px;
`;
const StyledPitchTypeContainer = styled.div`
&& {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    > div {
        width: 49%;
    }
}`;
const StyledDateRangeContainer = styled.div`
    font-size: 1.25em;
    > button {
        margin: 10px;
    }
`;
const StyledPrevNextContainer = styled.div`
    text-align: left;
    font-size: 1.2em;
    font-weight: bold;
`;
const StyledPrevNextButton = styled.button`
    && {
        font-size: 0.7em !important;
        background-color: transparent;
        color: #2671ce;
        border: none;
        padding: 0 5px;
        &:focus {
            outline: none;
            border: none;
        }
        transform: rotate(${(props) => props.rotation}deg);
}
`;
const StyledVideoContainer = styled.div`
    max-width: 1024px;
    margin: auto;
`;

const buildQueries = (playerid, startdate, enddate, levelid) => {
    const startYearID = moment(startdate).year();
    const endYearID = moment(enddate).year();
    const yearid = startYearID === endYearID ? startYearID : null;
    const queries = [
        {
            queryKey: ['blockingReport', playerid, levelid, yearid, startdate, enddate],
            queryFn: yearid
                ? (t) => PlayerReport.blok(t, { playerid, yearid, levelid })
                : (t) => PlayerReport.blok(t, { playerid, startdate, enddate, levelid }),
        },
    ];
    return queries;
};

const mapDataResponse = (response) => {
    const blokData = response[0].data;
    return { blokData };
};

const getCatcherVideo = (pitchLog = [], user) => {
    const playlist = mapPlaylistFromVideoSearch({ videos: pitchLog, playervideos: [] }, user.Roles.map((r) => r.RoleName));
    return playlist;
};

const getPitchVideo = (pitchLog = [], pitchInfo = {}, user) => {
    const { gameid, pitchseq, eventseq } = pitchInfo;

    const video = pitchLog.find((p) => p.gameid === gameid && p.pitchseq === pitchseq && p.eventseq === eventseq);
    return mapPlaylistFromVideoSearch({ videos: [video], playervideos: [] }, user.Roles.map((r) => r.RoleName));
};

// #region Filters
const filterEvents = (events, filters) => {
    const { pitchType, batter, zone, runner, advance } = filters;
    const filteredEvents = events.filter((e) => (!pitchType.filterFn || pitchType.filterFn(e))
        && (!batter.filterFn || batter.filterFn(e))
        && (!zone.filterFn || zone.filterFn(e))
        && (!runner.filterFn || runner.filterFn(e))
        && (!advance.filterFn || advance.filterFn(e)));

    return filteredEvents;
};

const pitchTypeOptions = (translate) => [
    { name: translate('sections.reports.catcher.blocking.show-all'), filterFn: null },
    { name: translate('baseball.pitches.abbr-fastball'), filterFn: (x) => [1, 9, 10].indexOf(x.pitchtype) > -1 },
    { name: translate('baseball.pitches.abbr-curveball'), filterFn: (x) => x.pitchtype === 2 },
    { name: `${translate('baseball.pitches.abbr-slider')}/${translate('baseball.pitches.abbr-cutter')}`, filterFn: (x) => [3, 8].indexOf(x.pitchtype) > -1 },
    { name: `${translate('baseball.pitches.abbr-changeup')}/${translate('baseball.pitches.abbr-splitter')}`, filterFn: (x) => [4, 7].indexOf(x.pitchtype) > -1 },
];

const batterOptions = (translate) => [
    { name: translate('sections.reports.catcher.blocking.show-all'), filterFn: null },
    { name: translate('baseball.rhh'), filterFn: (x) => !x.islhb },
    { name: translate('baseball.lhh'), filterFn: (x) => x.islhb },
];

const zoneOptions = (translate) => [
    { name: translate('sections.reports.catcher.blocking.show-all'), filterFn: null },
    { name: translate('sections.reports.catcher.blocking.short'), filterFn: (x) => x.short },
    { name: translate('sections.reports.catcher.blocking.long'), filterFn: (x) => x.long },
    { name: translate('sections.reports.catcher.blocking.middle'), filterFn: (x) => x.middle },
    { name: translate('sections.reports.catcher.blocking.glove-side'), filterFn: (x) => x.gloveside },
    { name: translate('sections.reports.catcher.blocking.arm-side'), filterFn: (x) => x.armside },
];

const runnerOptions = (translate) => [
    { name: translate('sections.reports.catcher.blocking.show-all'), filterFn: null },
    { name: 'R1', filterFn: (x) => x.r1 },
    { name: 'R2', filterFn: (x) => x.r2 },
    { name: 'R3', filterFn: (x) => x.r3 },
    { name: 'R13', filterFn: (x) => x.r13 },
    { name: '2K', filterFn: (x) => x.twok },
];

const advanceOptions = (translate) => [
    { name: translate('sections.reports.catcher.blocking.show-all'), filterFn: null },
    { name: translate('sections.reports.catcher.blocking.runners-advance'), filterFn: (x) => x.advr },
    { name: translate('sections.reports.catcher.blocking.runners-not-advance'), filterFn: (x) => !x.advr },
];
// #endregion

const BlockingReport = ({ playerID, startYear, endYear, level: levelid }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const [playlist, setPlaylist] = useState([]);
    const [filters, setFilters] = useState({
        pitchType: pitchTypeOptions(translate)[0],
        batter: batterOptions(translate)[0],
        zone: zoneOptions(translate)[0],
        runner: runnerOptions(translate)[0],
        advance: advanceOptions(translate)[0],
    });
    const [gameGroup, setGameGroup] = useState({
        gameGroupIds: [],
        hasPrevGameGroup: true,
        hasNextGameGroup: false,
        rangeSize: 30,
    });

    const startdate = moment(`${startYear}-01-01`).format('YYYY-MM-DD');
    const enddate = moment(`${endYear}-12-31`).format('YYYY-MM-DD');

    const dataQueries = buildQueries(playerID, startdate, enddate, levelid);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { blokData } = mapDataResponse(dataQueriesResponse);
    const { events, games, totals } = blokData;

    if (games.length === 0) {
        return <div>{translate('sections.reports.catcher.blocking.no-data')}</div>;
    }

    // #region Game group
    const { gameGroupIds, hasNextGameGroup, hasPrevGameGroup } = gameGroup;
    const intitalGroupIds = games?.sort((a, b) => moment(a.gamedate) - moment(b.gamedate)).slice(games.length - 30, games.length).map((g) => g.gameid);
    const group = gameGroupIds.length > 0 ? gameGroupIds : intitalGroupIds;
    const firstGame = games.find((g) => g.gameid === group[0]) || {};
    const lastGame = games.find((g) => g.gameid === group[group.length - 1]) || {};
    const groupLabel = `${internationalizeDate(translate, firstGame.gamedate, false, false, true, true, false, false, false, false)} - ${internationalizeDate(translate, lastGame.gamedate, false, false, true, true, false, false, false, false)} (${group.length} ${translate('baseball.abbr-game')})`;
    const groupEvents = events.filter((e) => group.indexOf(e.gameid) > -1) || [];

    const setNextGameGroup = () => {
        let hasNext = false;
        let newGameIds = [];
        const gameIds = games.sort((a, b) => moment(a.gamedate) - moment(b.gamedate)).map((g) => g.gameid);
        const currentGroupIds = gameGroup.gameGroupIds.length > 0 ? gameGroup.gameGroupIds : intitalGroupIds;
        const lastGameId = currentGroupIds[currentGroupIds.length - 1];
        const lastGameIndex = gameIds.indexOf(lastGameId);
        if (gameIds.length - lastGameIndex > 30) {
            newGameIds = gameIds.slice(lastGameIndex + 1, lastGameIndex + 31);
            hasNext = true;
        } else {
            newGameIds = gameIds.slice(games.length - 30, games.length);
            hasNext = false;
        }
        setGameGroup({ gameGroupIds: newGameIds, hasNextGameGroup: hasNext, hasPrevGameGroup: true });
    };
    const setPrevGameGroup = () => {
        let hasPrev = false;
        let newGameIds = [];
        const gameIds = games.sort((a, b) => moment(a.gamedate) - moment(b.gamedate)).map((g) => g.gameid);
        const currentGroupIds = gameGroup.gameGroupIds.length > 0 ? gameGroup.gameGroupIds : intitalGroupIds;
        const firstGameId = currentGroupIds[0];
        const firstGameIndex = gameIds.indexOf(firstGameId);
        if (firstGameIndex - 30 > 0) {
            newGameIds = gameIds.slice(firstGameIndex - 30, firstGameIndex);
            hasPrev = true;
        } else {
            newGameIds = gameIds.slice(0, 30);
            hasPrev = false;
        }
        setGameGroup({ gameGroupIds: newGameIds, hasNextGameGroup: true, hasPrevGameGroup: hasPrev });
    };
    // #endregion

    // Overall Rows
    const overallTotalRow = totals?.find((x) => x.sortorder === 0) || {};
    const overallLgaRow = totals?.find((x) => x.sortorder === 2) || {};
    const overallGroupRow = getRowData(translate, groupEvents, group, games);

    // Split Rows
    const filteredEvents = filterEvents(events, filters);
    const totalRows = getSplitRows(translate, filteredEvents);
    const filteredGroupEvents = filterEvents(groupEvents, filters);
    const groupRows = getSplitRows(translate, filteredGroupEvents);

    /* Video */
    const allVideosPlaylist = getCatcherVideo(filteredEvents, user);
    const allGroupVideosPlaylist = getCatcherVideo(filteredGroupEvents, user);

    return (
        <>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard>
                        <StyledPrevNextContainer>
                            {hasPrevGameGroup && (
                                <StyledPrevNextButton rotation={90} onClick={setPrevGameGroup}>
                                    <Icon icon="chevron-down" />
                                </StyledPrevNextButton>
                            )}
                            {groupLabel}
                            {hasNextGameGroup && (
                                <StyledPrevNextButton rotation={-90} onClick={setNextGameGroup}>
                                    <Icon icon="chevron-down" />
                                </StyledPrevNextButton>
                            )}
                        </StyledPrevNextContainer>
                        <hr />
                        <StyledRow>
                            <Col lg={{ span: 4 }}>
                                <BlockingReportTable
                                    tableType="overall"
                                    rows={[overallTotalRow, overallGroupRow, overallLgaRow]}
                                    superheaderName={translate('sections.reports.catcher.blocking.overall')}
                                    pitches={filteredEvents}
                                    onClick={setPlaylist}
                                />
                            </Col>
                            <Col lg={{ span: 4 }}>
                                <BlockingReportTable
                                    tableType="total"
                                    rows={[overallTotalRow, overallGroupRow, overallLgaRow]}
                                    superheaderName={translate('sections.reports.catcher.blocking.totals')}
                                    pitches={filteredEvents}
                                    onClick={setPlaylist}
                                />
                            </Col>
                            <Col lg={{ span: 4 }}>
                                <BlockingReportTable
                                    tableType="bouncelocation"
                                    rows={[overallTotalRow, overallGroupRow, overallLgaRow]}
                                    superheaderName={translate('sections.reports.catcher.blocking.bounce-location')}
                                    pitches={filteredEvents}
                                    onClick={setPlaylist}
                                />
                            </Col>
                        </StyledRow>
                        <StyledRow>
                            <Col lg={{ span: 4 }}>&nbsp;</Col>
                            <Col lg={{ span: 4 }}>
                                <BlockingReportTable
                                    tableType="pitchtype"
                                    rows={[overallTotalRow, overallGroupRow, overallLgaRow]}
                                    superheaderName={translate('sections.reports.catcher.blocking.pitch-type')}
                                    pitches={filteredEvents}
                                    onClick={setPlaylist}
                                />
                            </Col>
                            <Col lg={{ span: 4 }}>
                                <BlockingReportTable
                                    tableType="situation"
                                    rows={[overallTotalRow, overallGroupRow, overallLgaRow]}
                                    superheaderName={translate('sections.reports.catcher.blocking.situations')}
                                    pitches={filteredEvents}
                                    onClick={setPlaylist}
                                />
                            </Col>
                        </StyledRow>
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard>
                        <StyledVideoContainer>
                            {playlist?.length ? <VideoPlayer playlist={playlist} /> : <h4>{translate('sections.reports.catcher.blocking.no-videos')}</h4>}
                        </StyledVideoContainer>
                    </ReportCard>
                </Col>
            </StyledRow>
            <StyledRow>
                <Col sm={{ span: 12 }}>
                    <ReportCard>
                        <StyledFiltersContainer>
                            <StyledFiltersLabel>{translate('sections.reports.catcher.blocking.pitch-type')}:</StyledFiltersLabel>
                            <Dropdown
                                options={pitchTypeOptions(translate)}
                                value={filters.pitchType}
                                onSelect={(value) => setFilters({ ...filters, pitchType: value })}
                            />
                            <StyledFiltersLabel>{translate('baseball.batter')}:</StyledFiltersLabel>
                            <Dropdown
                                options={batterOptions(translate)}
                                value={filters.batter}
                                onSelect={(value) => setFilters({ ...filters, batter: value })}
                            />
                            <StyledFiltersLabel>{translate('sections.reports.catcher.blocking.zone')}:</StyledFiltersLabel>
                            <Dropdown
                                options={zoneOptions(translate)}
                                value={filters.zone}
                                onSelect={(value) => setFilters({ ...filters, zone: value })}
                            />
                            <StyledFiltersLabel>{translate('baseball.runners_one')}:</StyledFiltersLabel>
                            <Dropdown
                                options={runnerOptions(translate)}
                                value={filters.runner}
                                onSelect={(value) => setFilters({ ...filters, runner: value })}
                            />
                            <StyledFiltersLabel>{translate('sections.reports.catcher.blocking.advance')}:</StyledFiltersLabel>
                            <Dropdown
                                options={advanceOptions(translate)}
                                value={filters.advance}
                                onSelect={(value) => setFilters({ ...filters, advance: value })}
                            />
                        </StyledFiltersContainer>
                        <hr />
                        <StyledRow>
                            <Col lg={{ span: 1 }}>
                                <StyledDateRangeContainer>
                                    <strong>{translate('sections.reports.catcher.blocking.date-range')}:</strong>
                                    <div>{startYear === endYear ? startYear : `${startYear} - ${endYear}`}</div>
                                    <Button variant="primary" onClick={() => setPlaylist(allVideosPlaylist)}>{translate('sections.reports.catcher.blocking.play-all')}</Button>
                                </StyledDateRangeContainer>
                            </Col>
                            <Col lg={{ span: 5 }}>
                                <BlockingChart
                                    events={filteredEvents}
                                    isGroup={false}
                                    onClick={(pitchInfo) => setPlaylist(getPitchVideo(filteredEvents, pitchInfo, user))}
                                />
                            </Col>
                            <Col lg={{ span: 6 }}>
                                <div>
                                    <BlockingReportTable
                                        tableType="situation"
                                        rows={[totalRows.situation]}
                                        superheaderName={translate('sections.reports.catcher.blocking.situations')}
                                        pitches={filteredEvents}
                                        onClick={setPlaylist}
                                    />
                                </div>
                                <StyledPitchTypeContainer>
                                    <BlockingReportTable
                                        tableType="short"
                                        rows={[totalRows.shortFB, totalRows.shortCB, totalRows.shortSlCut, totalRows.shortChSpl]}
                                        superheaderName={translate('sections.reports.catcher.blocking.short')}
                                        pitches={filteredEvents}
                                        onClick={setPlaylist}
                                    />
                                    <BlockingReportTable
                                        tableType="long"
                                        rows={[totalRows.longFB, totalRows.longCB, totalRows.longSlCut, totalRows.longChSpl]}
                                        superheaderName={translate('sections.reports.catcher.blocking.long')}
                                        pitches={filteredEvents}
                                        onClick={setPlaylist}
                                    />
                                </StyledPitchTypeContainer>
                            </Col>
                        </StyledRow>
                        <br /><br />
                        <StyledRow>
                            <Col lg={{ span: 1 }}>
                                <StyledDateRangeContainer>
                                    <strong>{translate('sections.reports.catcher.blocking.date-range')}:</strong>
                                    <div>{groupLabel}</div>
                                    <Button variant="primary" onClick={() => setPlaylist(allGroupVideosPlaylist)}>{translate('sections.reports.catcher.blocking.play-all')}</Button>
                                </StyledDateRangeContainer>
                            </Col>
                            <Col lg={{ span: 5 }}>
                                <BlockingChart
                                    events={filteredGroupEvents}
                                    isGroup={false}
                                    onClick={(pitchInfo) => setPlaylist(getPitchVideo(filteredGroupEvents, pitchInfo, user))}
                                />
                            </Col>
                            <Col lg={{ span: 6 }}>
                                <div>
                                    <BlockingReportTable
                                        tableType="situation"
                                        rows={[groupRows.situation]}
                                        superheaderName={translate('sections.reports.catcher.blocking.situations')}
                                        pitches={filteredGroupEvents}
                                        onClick={setPlaylist}
                                    />
                                </div>
                                <StyledPitchTypeContainer>
                                    <BlockingReportTable
                                        tableType="short"
                                        rows={[groupRows.shortFB, groupRows.shortCB, groupRows.shortSlCut, groupRows.shortChSpl]}
                                        superheaderName={translate('sections.reports.catcher.blocking.short')}
                                        pitches={filteredGroupEvents}
                                        onClick={setPlaylist}
                                    />
                                    <BlockingReportTable
                                        tableType="long"
                                        rows={[groupRows.longFB, groupRows.longCB, groupRows.longSlCut, groupRows.longChSpl]}
                                        superheaderName={translate('sections.reports.catcher.blocking.long')}
                                        pitches={filteredGroupEvents}
                                        onClick={setPlaylist}
                                    />
                                </StyledPitchTypeContainer>
                            </Col>
                        </StyledRow>
                    </ReportCard>
                </Col>
            </StyledRow>
        </>
    );
};

BlockingReport.propTypes = {
    playerID: PropTypes.number.isRequired,
    startYear: PropTypes.number.isRequired,
    endYear: PropTypes.number.isRequired,
    level: PropTypes.number.isRequired,
};

export default BlockingReport;
